<script setup>
import { ref, watch, defineProps, defineModel, defineEmits, computed } from 'vue';
import {useI18n} from "vue-i18n";

const { t } = useI18n()
const props = defineProps({
    items: {
        type: Array,
        default: () => [],
    },
    label: {
        type: String
    },
    placeholder: {
        type: String
    },
    minChars: {
        type: Number,
        default: 3,
    },
    itemTitle: {
        type: String,
        default: 'name',
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: false
    }

});

const emit = defineEmits(['search']);
const model = defineModel();
const searchQuery = ref('');
const hasSearched = ref(false);
const requiredRule = (v) =>
    !!v || t('common.messages.validations.required')

const noDataText = computed(() => {
    const query = searchQuery.value || '';

    if (query.length < props.minChars) {
        return t('common.messages.info.enterAtLeastCharacters', { count: props.minChars });
    }

    if (props.isLoading) {
        return t('common.messages.info.searching') + "...";
    }

    if (hasSearched.value && props.items.length === 0) {
        return t('common.messages.info.noData');
    }

    return t('common.messages.info.searching') + "...";
});

const computedItems = computed(() => {
    const query = searchQuery.value || "";

    if (query.length < props.minChars) {
        return [];
    }

    return props.items;
});


let searchTimeout = null;

watch(searchQuery, (newValue) => {
    clearTimeout(searchTimeout);

    hasSearched.value = false;

    if (!newValue || newValue.length < props.minChars) {
        emit('search', '');
        return;
    }

    searchTimeout = setTimeout(() => {
        hasSearched.value = true;
        emit('search', newValue);
    }, 500);
});

function removeItem(item) {
    if (Array.isArray(model.value)) {
        model.value = model.value.filter(i => JSON.stringify(i) !== JSON.stringify(item.raw))
    } else {
        model.value = null
    }
}


const inputClasses = computed(() => ({
    'field-default': true,
    'input-disabled': props.readonly
}))

const normalizedModel = computed({
    get: () => model.value,
    set: (val) => {
        if (Array.isArray(val)) {
            model.value = val.filter(v => v && v !== '');
        } else if (val === '') {
            model.value = null;
        } else {
            model.value = val;
        }
    }
});


</script>

<template>
    <v-autocomplete
        v-model="normalizedModel"
        :loading="isLoading"
        :items="computedItems"
        :no-data-text="noDataText"
        :item-title="itemTitle"
        :label="label || t('common.messages.info.select')"
        :min-chars="minChars"
        :readonly="readonly"
        v-model:search="searchQuery"
        :clearable="!readonly"
        :class="inputClasses"
        autocomplete="off"
        return-object
        :multiple="multiple"
        :rules="props.required ? [requiredRule] : []"
    >
        <!-- Slot para personalizar os chips -->
        <template #chip="{ props, item, index }">
            <template v-if="item?.raw && item.raw[itemTitle] && item.raw[itemTitle].trim() !== ''">
                <v-chip
                    v-bind="props"
                    :closable="!readonly"
                    @click:close="!readonly && removeItem(item)"
                >
                    {{ item.raw[itemTitle] }}
                </v-chip>
            </template>
        </template>
    </v-autocomplete>
</template>

